import React, { useState, useEffect, useRef, useId } from 'react';
import { auth } from '../../firebase/config';
import { apiClient, API_ENDPOINTS } from '../../config/api';
import { getActiveRoleFromStorage } from '../../utils/getActiveRole';
import {
  splitInviteeText, buildInviteeText, isValidEmail, contactSuggestions,
} from '../../utils/calendarInvites';

const SUGGESTION_LIMIT = 8;
const DEBOUNCE_MS = 250;
// Mirrors the mobile InviteeInput: nothing fetched or shown until the user has
// typed at least 3 characters, so single-letter noise doesn't open the list
// or hit the contacts endpoint.
const MIN_SEARCH_LEN = 3;

const ownEmailFromStorage = () => {
  try { return (JSON.parse(localStorage.getItem('user') || '{}')).email || ''; } catch { return ''; }
};

// Guests field for the Add Event dialog: chips + autocomplete from the user's
// Rialty contacts (GET /users/me/contacts), and any other valid email address can
// be typed in and added too. `value` is one string (see splitInviteeText); the
// parent validates it on Save, so an uncommitted draft is never silently dropped.
// `lockedEmails` are guests already on the event — shown but not removable, because
// guests can't be removed from the dialog yet.
export default function InviteeInput({ value, onChange, lockedEmails = [], placeholder }) {
  const { committed, draft } = splitInviteeText(value);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [draftError, setDraftError] = useState(false);
  const requestId = useRef(0);
  const listId = useId();
  const ownEmail = useRef(ownEmailFromStorage()).current;
  const locked = new Set(lockedEmails.map((e) => String(e).toLowerCase()));

  // Contacts matching what's being typed. Suppressed until the draft has at
  // least MIN_SEARCH_LEN characters so single-letter noise doesn't open the
  // list or hit the contacts endpoint.
  useEffect(() => {
    if (!open) return undefined;
    if (draft.trim().length < MIN_SEARCH_LEN) {
      setContacts([]);
      return undefined;
    }
    const id = ++requestId.current;
    const timer = setTimeout(async () => {
      try {
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        const viewingRole = getActiveRoleFromStorage();
        const resp = await apiClient.get(API_ENDPOINTS.userContacts, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params: {
            size: SUGGESTION_LIMIT * 2,
            ...(draft.trim() ? { query: draft.trim() } : {}),
            ...(viewingRole ? { viewingRole } : {}),
          },
        });
        if (id !== requestId.current) return;
        const d = resp && resp.data;
        setContacts(Array.isArray(d) ? d : ((d && (d.content || d.data)) || []));
      } catch {
        if (id === requestId.current) setContacts([]);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, draft]);

  const suggestions = contactSuggestions(contacts, { committed, ownEmail, limit: SUGGESTION_LIMIT });
  const draftIsNewEmail = isValidEmail(draft)
    && !committed.some((e) => e.toLowerCase() === draft.trim().toLowerCase())
    && draft.trim().toLowerCase() !== ownEmail.toLowerCase();
  const rows = [
    ...suggestions.map((s) => ({ ...s, kind: 'contact' })),
    ...(draftIsNewEmail && !suggestions.some((s) => s.email.toLowerCase() === draft.trim().toLowerCase())
      ? [{ email: draft.trim(), name: '', kind: 'new' }] : []),
  ];

  const commit = (email) => {
    const already = committed.some((e) => e.toLowerCase() === email.toLowerCase());
    onChange(buildInviteeText(already ? committed : [...committed, email], ''));
    setDraftError(false);
    setActive(-1);
  };

  const removeChip = (index) => {
    const next = committed.filter((_, i) => i !== index);
    onChange(buildInviteeText(next, draft));
  };

  const onInputChange = (e) => {
    setDraftError(false);
    setActive(-1);
    setOpen(true);
    onChange(buildInviteeText(committed, e.target.value));
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' && rows.length) {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % rows.length);
    } else if (e.key === 'ArrowUp' && rows.length) {
      e.preventDefault();
      setActive((i) => (i <= 0 ? rows.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      if (active >= 0 && rows[active]) {
        e.preventDefault();
        commit(rows[active].email);
      } else if (draft.trim()) {
        e.preventDefault();
        if (isValidEmail(draft)) commit(draft.trim()); else setDraftError(true);
      }
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    } else if (e.key === 'Backspace' && !draft && committed.length) {
      const last = committed.length - 1;
      if (!locked.has(committed[last].toLowerCase())) removeChip(last);
    }
  };

  const onBlur = () => {
    // Keep a typed, valid address instead of losing it when focus moves on.
    if (draft.trim() && isValidEmail(draft)) commit(draft.trim());
    setOpen(false);
  };

  return (
    <div className="acal-invitee">
      <div className={`acal-invitee-box${draftError ? ' acal-invitee-box-error' : ''}`}>
        {committed.map((email, i) => {
          const isLocked = locked.has(email.toLowerCase());
          const valid = isValidEmail(email);
          return (
            <span
              key={`${email}-${i}`}
              className={`acal-invitee-chip${valid ? '' : ' acal-invitee-chip-invalid'}`}
              title={valid ? email : `${email} isn't a valid email address`}
            >
              {email}
              {!isLocked && (
                <button
                  type="button"
                  className="acal-invitee-chip-x"
                  aria-label={`Remove ${email}`}
                  onClick={() => removeChip(i)}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
        <input
          className="acal-invitee-input"
          type="text"
          name="invitees"
          autoComplete="off"
          placeholder={committed.length ? '' : (placeholder || 'Search contacts or type an email address')}
          value={draft}
          onChange={onInputChange}
          onFocus={() => setOpen(true)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={open && rows.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
        />
      </div>
      {draftError && <p className="acal-invitee-error">Enter a valid email address, like name@example.com.</p>}
      {open && draft.trim().length >= MIN_SEARCH_LEN && (
        <ul id={listId} className="acal-invitee-list" role="listbox">
          {rows.map((r, i) => (
            <li
              key={`${r.kind}-${r.email}`}
              role="option"
              aria-selected={i === active}
              className={`acal-invitee-option${i === active ? ' acal-invitee-option-active' : ''}`}
              // mousedown (not click) so the input doesn't blur — and commit its draft — first
              onMouseDown={(e) => { e.preventDefault(); commit(r.email); }}
            >
              {r.kind === 'contact' ? (
                <>
                  <span className="acal-invitee-option-name">{r.name || r.email}</span>
                  {r.name && <span className="acal-invitee-option-email">{r.email}</span>}
                </>
              ) : (
                <span className="acal-invitee-option-name">Add &ldquo;{r.email}&rdquo;</span>
              )}
            </li>
          ))}
          {rows.length === 0 && (
            <li className="acal-invitee-option acal-invitee-option-empty" aria-disabled="true">
              No matching contacts — type a full email to add
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
